import { useEffect, useMemo, useState } from 'react';

import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { optimizationFieldHelp } from '../../app/guidance/fieldHelpRegistry';
import { optimizationApi } from './api';
import type { OptimizationApi } from './api';
import { SolutionDetails } from './SolutionDetails';
import type {
  OptimizationMode,
  OptimizationRequestInput,
  OptimizationRunRecord,
  OptimizationSubmission,
} from './types';

import './optimization.css';

const ZERO_HASH = '0'.repeat(64);

export interface OptimizationWorkspaceProps {
  api?: OptimizationApi;
  initialRequest?: OptimizationRequestInput;
  pollIntervalMs?: number;
}

function proofWording(run: OptimizationRunRecord): string {
  if (run.recommended_solution !== null) {
    if (run.minimum_count_proven) {
      return run.secondary_objectives_proven
        ? 'Minimum pump count and secondary ordering proven.'
        : 'Minimum pump count proven; secondary ordering is best found.';
    }
    return 'Best feasible design found — optimality is not proven.';
  }
  if (run.conclusion === 'proven_infeasible') {
    return 'Proven infeasible in the bounded discrete search space.';
  }
  return 'No feasible design found yet — infeasibility is not proven.';
}

function outcomeFor(run: OptimizationRunRecord): {
  label:
    | 'Does not meet requirement'
    | 'Meets requirement'
    | 'No feasible result yet'
    | 'Searching';
  tone: 'danger' | 'pending' | 'success' | 'warning';
} {
  if (run.recommended_solution !== null) {
    return { label: 'Meets requirement', tone: 'success' };
  }
  if (run.conclusion === 'proven_infeasible') {
    return { label: 'Does not meet requirement', tone: 'danger' };
  }
  if (run.state === 'queued' || run.state === 'running') {
    return { label: 'Searching', tone: 'pending' };
  }
  return { label: 'No feasible result yet', tone: 'warning' };
}

function readableState(value: string): string {
  return value.replaceAll('_', ' ');
}

export function OptimizationWorkspace({
  api = optimizationApi,
  initialRequest,
  pollIntervalMs = 1000,
}: OptimizationWorkspaceProps) {
  const [mode, setMode] = useState<OptimizationMode>('preview');
  const [budgetMinutes, setBudgetMinutes] = useState(15);
  const [seed, setSeed] = useState(0);
  const [allowDisabling, setAllowDisabling] = useState(false);
  const [run, setRun] = useState<OptimizationRunRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [runAction, setRunAction] = useState<'cancel' | 'resume' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const latestProgress = useMemo(
    () => run?.progress_events.at(-1) ?? null,
    [run],
  );
  const active = run?.state === 'queued' || run?.state === 'running';
  const locked = busy || active || runAction !== null;
  const preparedNetwork = (initialRequest?.network_project ?? null) as {
    candidate_pump_sites?: Array<{ id: string; name: string }>;
    name?: string;
    scenarios?: Array<{ id: string; name: string; required?: boolean }>;
  } | null;
  const preparedFleet = (initialRequest?.fleet_units ?? []) as Array<{
    availability?: string;
    physical_identifier?: string;
  }>;
  const preparedHoses = (initialRequest?.hose_types ?? []) as Array<{
    manufacturer?: string;
    model?: string;
  }>;
  const outcome = run === null ? null : outcomeFor(run);

  useEffect(() => {
    if (!active || run === null) return undefined;
    const timer = window.setInterval(() => {
      void api
        .get(run.id)
        .then((next) => {
          setRun(next);
          setError(null);
        })
        .catch((cause: unknown) => {
          setError(
            cause instanceof Error
              ? cause.message
              : 'Run status could not be refreshed.',
          );
        });
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [active, api, pollIntervalMs, run]);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      if (!Number.isFinite(budgetMinutes) || budgetMinutes <= 0) {
        throw new Error('Time budget must be a finite positive number.');
      }
      if (!Number.isInteger(seed) || seed < 0) {
        throw new Error('Deterministic seed must be a nonnegative integer.');
      }
      if (initialRequest === undefined) {
        throw new Error(
          'Prepare the current project revision before starting optimization.',
        );
      }
      const placeholder: OptimizationSubmission = {
        allow_scenario_unit_disabling: allowDisabling,
        request: {
          ...initialRequest,
          deterministic_seed: seed,
          input_hash: ZERO_HASH,
          mode,
          time_budget_s: budgetMinutes * 60,
        },
        site_orientation_policies: [],
      };
      const inputHash = await api.canonicalHash(placeholder);
      const accepted = await api.create({
        ...placeholder,
        request: { ...placeholder.request, input_hash: inputHash },
      });
      setRun(await api.get(accepted.run_id));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Optimization could not be started.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (run === null) return;
    setRunAction('cancel');
    try {
      setRun(await api.cancel(run.id));
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Cancellation could not be requested.',
      );
    } finally {
      setRunAction(null);
    }
  }

  async function resume() {
    if (run === null) return;
    setRunAction('resume');
    try {
      if (!Number.isFinite(budgetMinutes) || budgetMinutes <= 0) {
        throw new Error('Added time budget must be a finite positive number.');
      }
      const accepted = await api.resume(run.id, budgetMinutes * 60);
      setRun(await api.get(accepted.run_id));
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Run could not be resumed.',
      );
    } finally {
      setRunAction(null);
    }
  }

  async function refresh() {
    if (run === null) return;
    try {
      setRun(await api.get(run.id));
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Run status could not be refreshed.',
      );
    }
  }

  return (
    <section
      className="optimization-workspace"
      aria-labelledby="optimization-readiness-title"
    >
      <header className="optimization-readiness">
        <h2 id="optimization-readiness-title">
          {initialRequest === undefined ? 'Needs setup' : 'Ready to search'}
        </h2>
        {initialRequest === undefined ? (
          <p>Prepare the current project revision.</p>
        ) : (
          <p>{preparedNetwork?.name ?? 'Prepared project'}</p>
        )}
      </header>

      <div className="optimization-controls">
        <label>
          Search mode
          <select
            {...guided(optimizationFieldHelp.mode)}
            disabled={locked}
            value={mode}
            onChange={(event) =>
              setMode(event.target.value as OptimizationMode)
            }
          >
            <option value="preview">Preview — best found</option>
            <option value="thorough">Thorough — bounded proof</option>
          </select>
        </label>
      </div>

      <details className="optimization-details">
        <summary>Search options</summary>
        <div className="optimization-controls">
          <label>
            Time budget (minutes)
            <input
              {...guided(optimizationFieldHelp.budget)}
              disabled={locked}
              min="0.01"
              step="1"
              type="number"
              value={budgetMinutes}
              onChange={(event) => setBudgetMinutes(Number(event.target.value))}
            />
          </label>
          <label>
            Deterministic seed
            <input
              {...guided(optimizationFieldHelp.seed)}
              disabled={locked}
              min="0"
              type="number"
              value={seed}
              onChange={(event) => setSeed(Number(event.target.value))}
            />
          </label>
          <label className="optimization-check">
            <input
              {...guided(optimizationFieldHelp.scenarioBypass)}
              checked={allowDisabling}
              disabled={locked}
              type="checkbox"
              onChange={(event) => setAllowDisabling(event.target.checked)}
            />
            Permit scenario-specific unit bypass
          </label>
        </div>
      </details>

      {initialRequest === undefined ? null : (
        <details className="optimization-details">
          <summary>Prepared inputs</summary>
          <div className="optimization-snapshot">
            <p>
              Input <code>{initialRequest.input_hash}</code>
            </p>
            <dl className="optimization-metrics">
              <div>
                <dt>Approved sites</dt>
                <dd>{preparedNetwork?.candidate_pump_sites?.length ?? 0}</dd>
              </div>
              <div>
                <dt>Required scenarios</dt>
                <dd>
                  {preparedNetwork?.scenarios?.filter(
                    (item) => item.required !== false,
                  ).length ?? 0}
                </dd>
              </div>
              <div>
                <dt>Physical fleet units</dt>
                <dd>{preparedFleet.length}</dd>
              </div>
              <div>
                <dt>Hose definitions</dt>
                <dd>{preparedHoses.length}</dd>
              </div>
            </dl>
            <p>
              Sites:{' '}
              {preparedNetwork?.candidate_pump_sites
                ?.map((item) => item.name)
                .join(', ') || 'none'}
            </p>
            <p>
              Scenarios:{' '}
              {preparedNetwork?.scenarios?.map((item) => item.name).join(', ') ||
                'none'}
            </p>
            <p>
              Fleet:{' '}
              {preparedFleet
                .map((item) => item.physical_identifier ?? 'Unnamed unit')
                .join(', ') || 'none'}
            </p>
          </div>
        </details>
      )}

      <button
        className="button button--primary optimization-start"
        disabled={locked || initialRequest === undefined}
        type="button"
        onClick={() => void start()}
      >
        {busy
          ? 'Starting…'
          : mode === 'preview'
            ? 'Run preview'
            : 'Run thorough search'}
      </button>
      {error === null || run !== null ? null : <p role="alert">{error}</p>}

      {run === null || outcome === null ? null : (
        <section
          className={`optimization-run optimization-run--${outcome.tone}`}
        >
          <div className="optimization-run-heading">
            <div aria-live="polite" role="status">
              <h3 className="optimization-outcome">{outcome.label}</h3>
              <p className="optimization-proof">{proofWording(run)}</p>
              <p className="optimization-run-state">
                Run status: {readableState(run.state)}.
              </p>
            </div>
            <div className="optimization-actions">
              {active ? (
                <button
                  disabled={runAction !== null}
                  type="button"
                  onClick={() => void cancel()}
                >
                  {runAction === 'cancel' ? 'Cancelling…' : 'Cancel'}
                </button>
              ) : null}
              {run.state === 'timed_out' ||
              run.state === 'cancelled' ||
              run.state === 'failed' ? (
                <button
                  disabled={runAction !== null}
                  type="button"
                  onClick={() => void resume()}
                >
                  {runAction === 'resume'
                    ? 'Resuming…'
                    : 'Resume with added budget'}
                </button>
              ) : null}
            </div>
          </div>

          {error === null ? null : (
            <div>
              <p role="alert">{error}</p>
              <button type="button" onClick={() => void refresh()}>
                Retry status refresh
              </button>
            </div>
          )}
          {active ? (
            <progress
              aria-label="Optimization progress"
              max={1}
              value={latestProgress?.completed_fraction ?? 0}
            />
          ) : null}
          {run.failure_message === null ? null : (
            <p role="alert">{run.failure_message}</p>
          )}
          {run.bounded_search_audit?.configured_rpm_domain_complete ===
          false ? (
            <p className="optimization-warning">
              Verified curves do not cover every configured RPM state. Pump-count
              exhaustion and secondary ordering remain unproven.
            </p>
          ) : null}

          {run.recommended_solution === null ? null : (
            <SolutionDetails
              label="Recommended plan"
              run={run}
              solution={run.recommended_solution}
            />
          )}

          {run.recommended_solution === null &&
          run.rejected_constraints.length > 0 ? (
            <section className="optimization-limits">
              <h4>Limiting checks</h4>
              <ul>
                {run.rejected_constraints.map((item) => (
                  <li key={`${item.code}-${item.message}`}>{item.message}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {run.alternatives.length === 0 ? null : (
            <details className="optimization-details">
              <summary>Alternatives ({run.alternatives.length})</summary>
              <div className="optimization-alternatives">
                {run.alternatives.map((solution, index) => (
                  <SolutionDetails
                    {...(run.recommended_solution === null
                      ? {}
                      : {
                          baseline: run.recommended_solution,
                        })}
                    key={index}
                    label={`Alternative ${index + 1}`}
                    run={run}
                    solution={solution}
                  />
                ))}
              </div>
            </details>
          )}

          <details className="optimization-details">
            <summary>Search details</summary>
            <dl className="optimization-metrics">
              <div>
                <dt>Run ID</dt>
                <dd>{run.id}</dd>
              </div>
              <div>
                <dt>State</dt>
                <dd>{readableState(run.state)}</dd>
              </div>
              <div>
                <dt>Elapsed</dt>
                <dd>{run.elapsed_time_s.toFixed(1)} s</dd>
              </div>
              <div>
                <dt>Current count</dt>
                <dd>{run.checkpoint.current_pump_count}</dd>
              </div>
              <div>
                <dt>Evaluated</dt>
                <dd>{run.checkpoint.configurations_evaluated}</dd>
              </div>
              <div>
                <dt>Pruned</dt>
                <dd>{run.checkpoint.configurations_pruned}</dd>
              </div>
              <div>
                <dt>Proven lower bound</dt>
                <dd>{run.checkpoint.minimum_pump_count_lower_bound}</dd>
              </div>
              <div>
                <dt>Feasible upper bound</dt>
                <dd>{run.checkpoint.best_feasible_pump_count_bound ?? '—'}</dd>
              </div>
              <div>
                <dt>Remaining search</dt>
                <dd>{run.checkpoint.remaining_configuration_hint ?? '—'}</dd>
              </div>
              <div>
                <dt>Proof state</dt>
                <dd>{readableState(run.proof_state)}</dd>
              </div>
            </dl>
            {latestProgress === null ? null : <p>{latestProgress.message}</p>}
            {run.rejected_constraints.length === 0 ? null : (
              <ul>
                {run.rejected_constraints.map((item) => (
                  <li key={`detail-${item.code}-${item.message}`}>
                    {item.message} ({item.rejection_count.toLocaleString()}{' '}
                    states)
                  </li>
                ))}
              </ul>
            )}
          </details>
        </section>
      )}
    </section>
  );
}
